import type { ExprNode, BinOp, LValuePart, UnaryOp } from "./ast";

/**
 * 式の構文解析: 文字列 → ExprNode。
 * 再帰下降パーサ。優先度低→高:
 *   OrExpr → AndExpr → NotExpr → CmpExpr → AddExpr → MulExpr → UnaryExpr → Primary
 */

class ExprParser {
  private pos = 0;
  constructor(private src: string) {}

  parse(): ExprNode {
    const e = this.parseOr();
    this.skipWs();
    if (this.pos < this.src.length) {
      throw new Error(`式の末尾にトークンが残っています: '${this.src.slice(this.pos)}' (full: '${this.src}')`);
    }
    return e;
  }

  private parseOr(): ExprNode {
    let left = this.parseAnd();
    while (this.matchOp("||")) {
      const right = this.parseAnd();
      left = { type: "BinOp", op: "||", left, right };
    }
    return left;
  }

  private parseAnd(): ExprNode {
    let left = this.parseNot();
    while (this.matchOp("&&")) {
      const right = this.parseNot();
      left = { type: "BinOp", op: "&&", left, right };
    }
    return left;
  }

  private parseNot(): ExprNode {
    this.skipWs();
    if (this.peek() === "!" && this.src[this.pos + 1] !== "=") {
      this.pos++;
      const arg = this.parseNot();
      return { type: "UnaryOp", op: "!", arg };
    }
    return this.parseCmp();
  }

  private parseCmp(): ExprNode {
    const left = this.parseAdd();
    this.skipWs();
    const cmpOps = ["==", "!=", "<=", ">=", "<", ">"] as const;
    for (const op of cmpOps) {
      if (this.matchOp(op)) {
        const right = this.parseAdd();
        return { type: "BinOp", op, left, right };
      }
    }
    return left;
  }

  private parseAdd(): ExprNode {
    let left = this.parseMul();
    while (true) {
      this.skipWs();
      const ch = this.peek();
      if (ch === "+" || ch === "-") {
        this.pos++;
        const right = this.parseMul();
        left = { type: "BinOp", op: ch as BinOp, left, right };
      } else {
        break;
      }
    }
    return left;
  }

  private parseMul(): ExprNode {
    let left = this.parseUnary();
    while (true) {
      this.skipWs();
      const ch = this.peek();
      if (ch === "*" || ch === "/" || ch === "%") {
        this.pos++;
        const right = this.parseUnary();
        left = { type: "BinOp", op: ch as BinOp, left, right };
      } else {
        break;
      }
    }
    return left;
  }

  private parseUnary(): ExprNode {
    this.skipWs();
    if (this.peek() === "-") {
      this.pos++;
      const arg = this.parseUnary();
      return { type: "UnaryOp", op: "-" as UnaryOp, arg };
    }
    return this.parsePrimary();
  }

  private parsePrimary(): ExprNode {
    this.skipWs();
    if (this.pos >= this.src.length) {
      throw new Error(`予期せぬ式の終端: '${this.src}'`);
    }
    const ch = this.peek();

    // 括弧
    if (ch === "(") {
      this.pos++;
      const e = this.parseOr();
      this.skipWs();
      if (this.peek() !== ")") {
        throw new Error(`')' が必要です: '${this.src.slice(this.pos)}'`);
      }
      this.pos++;
      return e;
    }

    // 文字列リテラル
    if (ch === '"') {
      return { type: "String", value: this.readString() };
    }

    // 16進カラー
    if (ch === "#") {
      const start = this.pos;
      this.pos++;
      while (this.pos < this.src.length && /[0-9A-Fa-f]/.test(this.src[this.pos]!)) {
        this.pos++;
      }
      const hex = this.src.slice(start, this.pos);
      if (hex.length === 7) {
        return { type: "Number", value: parseInt(hex.slice(1), 16) };
      }
      throw new Error(`不正なカラーリテラル: ${hex}`);
    }

    // 数値
    if (/[0-9]/.test(ch)) {
      return { type: "Number", value: this.readNumber() };
    }

    // 識別子(変数 / 関数呼び出し / 予約語 true/false/null)
    if (/[A-Za-z_]/.test(ch)) {
      return this.readIdentExpr();
    }

    throw new Error(`予期しない文字: '${ch}' at ${this.pos} in '${this.src}'`);
  }

  private readString(): string {
    if (this.peek() !== '"') throw new Error("文字列開始符号がありません");
    this.pos++;
    let out = "";
    while (this.pos < this.src.length) {
      const c = this.src[this.pos]!;
      if (c === "\\") {
        this.pos++;
        const next = this.src[this.pos];
        if (next === undefined) throw new Error("文字列のエスケープが不完全");
        switch (next) {
          case "n":
            out += "\n";
            break;
          case "t":
            out += "\t";
            break;
          case "r":
            out += "\r";
            break;
          case '"':
            out += '"';
            break;
          case "\\":
            out += "\\";
            break;
          default:
            out += next;
        }
        this.pos++;
      } else if (c === '"') {
        this.pos++;
        return out;
      } else {
        out += c;
        this.pos++;
      }
    }
    throw new Error("文字列が閉じられていません");
  }

  private readNumber(): number {
    const start = this.pos;
    if (this.src.slice(this.pos, this.pos + 2) === "0x") {
      this.pos += 2;
      while (this.pos < this.src.length && /[0-9A-Fa-f]/.test(this.src[this.pos]!)) {
        this.pos++;
      }
      return parseInt(this.src.slice(start + 2, this.pos), 16);
    }
    while (this.pos < this.src.length && /[0-9]/.test(this.src[this.pos]!)) {
      this.pos++;
    }
    if (this.src[this.pos] === "." && /[0-9]/.test(this.src[this.pos + 1] ?? "")) {
      this.pos++;
      while (this.pos < this.src.length && /[0-9]/.test(this.src[this.pos]!)) {
        this.pos++;
      }
    }
    return parseFloat(this.src.slice(start, this.pos));
  }

  private readIdentExpr(): ExprNode {
    const name = this.readIdent();
    if (name === "true") return { type: "Bool", value: true };
    if (name === "false") return { type: "Bool", value: false };
    if (name === "null") return { type: "Null" };

    this.skipWs();
    // 関数呼び出し
    if (this.peek() === "(") {
      this.pos++;
      const args: ExprNode[] = [];
      this.skipWs();
      if (this.peek() !== ")") {
        args.push(this.parseOr());
        while (true) {
          this.skipWs();
          if (this.peek() === ",") {
            this.pos++;
            args.push(this.parseOr());
          } else break;
        }
      }
      this.skipWs();
      if (this.peek() !== ")") throw new Error(`関数引数の ')' が必要: ${name}`);
      this.pos++;
      return { type: "Call", name, args };
    }

    // 変数(`name.sub[i]` 形式)
    const path: LValuePart[] = [{ kind: "name", name }];
    while (true) {
      this.skipWs();
      const c = this.peek();
      if (c === ".") {
        this.pos++;
        const sub = this.readIdent();
        path.push({ kind: "name", name: sub });
      } else if (c === "[") {
        this.pos++;
        const indexExpr = this.parseOr();
        this.skipWs();
        if (this.peek() !== "]") throw new Error("配列添字の ']' が必要");
        this.pos++;
        path.push({ kind: "index", expr: indexExpr });
      } else {
        break;
      }
    }
    return { type: "Var", path };
  }

  private readIdent(): string {
    const start = this.pos;
    if (!/[A-Za-z_]/.test(this.src[this.pos] ?? "")) {
      throw new Error(`識別子が必要: at ${this.pos} in '${this.src}'`);
    }
    while (this.pos < this.src.length && /[A-Za-z0-9_]/.test(this.src[this.pos]!)) {
      this.pos++;
    }
    return this.src.slice(start, this.pos);
  }

  private skipWs(): void {
    while (this.pos < this.src.length && /\s/.test(this.src[this.pos]!)) {
      this.pos++;
    }
  }

  private peek(): string {
    return this.src[this.pos] ?? "";
  }

  private matchOp(op: string): boolean {
    this.skipWs();
    if (this.src.slice(this.pos, this.pos + op.length) === op) {
      this.pos += op.length;
      return true;
    }
    return false;
  }
}

export function parseExpr(src: string): ExprNode {
  return new ExprParser(src.trim()).parse();
}

// LValue を式パーサで読む(@set 用)
export function parseLValue(src: string): LValuePart[] {
  const expr = parseExpr(src);
  if (expr.type !== "Var") {
    throw new Error(`代入先は変数である必要があります: '${src}'`);
  }
  return expr.path;
}

// =================================================================
// 式の評価
// =================================================================

export interface VariableAccess {
  get(path: LValuePart[]): unknown;
  set(path: LValuePart[], value: unknown): void;
}

export interface FunctionRegistry {
  call(name: string, args: unknown[]): unknown;
}

export function evalExpr(expr: ExprNode, vars: VariableAccess, fns: FunctionRegistry): unknown {
  switch (expr.type) {
    case "Number":
      return expr.value;
    case "String":
      return expr.value;
    case "Bool":
      return expr.value;
    case "Null":
      return null;
    case "Var":
      return vars.get(expr.path);
    case "Call":
      return fns.call(
        expr.name,
        expr.args.map((a) => evalExpr(a, vars, fns)),
      );
    case "UnaryOp": {
      const v = evalExpr(expr.arg, vars, fns);
      if (expr.op === "-") return -toNumber(v);
      if (expr.op === "!") return !toBool(v);
      throw new Error(`未知の単項演算子: ${expr.op}`);
    }
    case "BinOp": {
      // 短絡評価
      if (expr.op === "&&") {
        const l = evalExpr(expr.left, vars, fns);
        if (!toBool(l)) return false;
        return toBool(evalExpr(expr.right, vars, fns));
      }
      if (expr.op === "||") {
        const l = evalExpr(expr.left, vars, fns);
        if (toBool(l)) return true;
        return toBool(evalExpr(expr.right, vars, fns));
      }
      const l = evalExpr(expr.left, vars, fns);
      const r = evalExpr(expr.right, vars, fns);
      switch (expr.op) {
        case "+":
          if (typeof l === "string" || typeof r === "string") {
            return String(l ?? "") + String(r ?? "");
          }
          return toNumber(l) + toNumber(r);
        case "-":
          return toNumber(l) - toNumber(r);
        case "*":
          return toNumber(l) * toNumber(r);
        case "/": {
          const rn = toNumber(r);
          if (rn === 0) throw new Error("0 で除算しました");
          return toNumber(l) / rn;
        }
        case "%": {
          const rn = toNumber(r);
          if (rn === 0) throw new Error("0 で剰余を取りました");
          return toNumber(l) % rn;
        }
        case "==":
          return looseEq(l, r);
        case "!=":
          return !looseEq(l, r);
        case "<":
          return toNumber(l) < toNumber(r);
        case "<=":
          return toNumber(l) <= toNumber(r);
        case ">":
          return toNumber(l) > toNumber(r);
        case ">=":
          return toNumber(l) >= toNumber(r);
        default:
          throw new Error(`未知の二項演算子: ${expr.op}`);
      }
    }
  }
}

function toNumber(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }
  if (typeof v === "boolean") return v ? 1 : 0;
  return 0;
}

function toBool(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return v.length > 0;
  return true;
}

function looseEq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === "number" || typeof b === "number") {
    return toNumber(a) === toNumber(b);
  }
  return false;
}

export { toNumber, toBool };
